import classNames from 'classnames';
import React, {useContext, useCallback} from 'react';

import {AsyncCourseDynamicPriceText, BrowseCourse} from '@udemy/browse-course';
import {InDemandCareer} from '@udemy/career-academies';
import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {IconButton} from '@udemy/react-core-components';
import {BlockList} from '@udemy/react-core-components';
import {ModalTrigger} from '@udemy/react-dialog-components';
import {ExerciseInfoModal} from '@udemy/react-exercise-info-modal';
import {ContentArea, ContentGrid, ContentGridItem} from '@udemy/react-layout-components';
import {Loader} from '@udemy/react-reveal-components';
import {PURCHASE_PRICE_TYPES} from '@udemy/shopping';
import {PlanPeriod} from '@udemy/subscription-browse';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';
import {useFormatCurrency} from '@udemy/ud-data';

import {CareerAcceleratorCTAType, CTAContext} from '../cta-context/cta-context';
import {PostCtaExperienceButton} from '../post-cta-experience-button/post-cta-experience-button.react-component';
import styles from './package-overview.module.less';

interface CourseStatProps {
    icon: React.ReactNode;
    value: React.ReactNode;
    label: React.ReactNode;
}

const CourseStat: React.FC<CourseStatProps> = ({icon, value, label}) => (
    <BlockList.Item>
        <div className={styles['course-stat']}>
            {icon}
            <div>
                <span className={classNames(styles['course-stat-value'])}>{value}</span>
                <span className={classNames(styles['course-stat-label'])}>{label}</span>
            </div>
        </div>
    </BlockList.Item>
);

export interface PackageOverviewProps {
    academy: InDemandCareer;
    courses?: BrowseCourse[];
}

export const PackageOverview = (props: PackageOverviewProps) => {
    const {academy, courses} = props;
    const {gettext, interpolate} = useI18n();
    const {formatCurrency} = useFormatCurrency();
    const personalPlanContext = useConsumerSubscriptionPlan();
    const isWiderThanMdMin = useMatchMedia('md-min');
    const {ctaType} = useContext(CTAContext);

    const getPlanPeriodProps = useCallback(
        (plan) => {
            return {
                listPriceText: formatCurrency(plan?.defaultPlanOption.price.monthly, {scale: 0}),
                discountPriceText: plan?.selectedDiscount?.subscriptionDiscountMonthlyPrice
                    ? formatCurrency(plan?.selectedDiscount?.subscriptionDiscountMonthlyPrice, {
                          scale: 0,
                      })
                    : undefined,
                showTrialDays: false,
                showStartingAtPrice: true,
                abbreviateStartingAtPrice: true,
                listPriceClassName: styles['personal-plan-price-text'],
                listPriceTextClassName: styles['personal-plan-price-text'],
                trialPeriodDays: plan?.trialPeriodDays,
            };
        },
        [formatCurrency],
    );

    if (!courses) {
        return null;
    }

    return (
        <div
            className={classNames('ud-full-viewport-container', styles['package-overview-wrapper'])}
        >
            <ContentArea className={classNames('ud-container')}>
                <ContentGrid>
                    <ContentGridItem
                        defaultColumn={'1 / span 4'}
                        mdColumn={'1 / span 12'}
                        lgColumn={'2 / span 10'}
                    >
                        <div className={styles['package-overview-container']}>
                            <div className={styles['heading-description-container']}>
                                <div className={classNames(styles['heading'])}>
                                    {academy.secondaryHeading ??
                                        interpolate(gettext('Break into a %s career.'), [
                                            academy.career.name,
                                        ])}
                                </div>
                                <div className={classNames('ud-text-lg', styles['description'])}>
                                    {academy.secondaryDescription}
                                </div>
                            </div>
                            <div>
                                <div className={classNames(styles['price-box'])}>
                                    <PostCtaExperienceButton
                                        academy={academy}
                                        uiRegion={'career_academies_middle'}
                                        courses={courses}
                                        allowAddToCartSuccessModal={false}
                                    />
                                    {ctaType === CareerAcceleratorCTAType.LOADING && <Loader />}
                                    {ctaType === CareerAcceleratorCTAType.PURCHASE_CTA_MX && (
                                        <div className={styles['price-text-container']}>
                                            <AsyncCourseDynamicPriceText
                                                buyables={courses}
                                                listPriceClassName={classNames(
                                                    'ud-text-xl',
                                                    styles['price-text'],
                                                )}
                                                discountPriceClassName={classNames(
                                                    'ud-heading-xl',
                                                    styles['price-text'],
                                                )}
                                                trackingEventContext={{
                                                    priceType: PURCHASE_PRICE_TYPES.bundle,
                                                }}
                                            />
                                        </div>
                                    )}

                                    {ctaType === CareerAcceleratorCTAType.PURCHASE_CTA_PP && (
                                        <div className={styles['personal-plan-disclaimer']}>
                                            <div>
                                                <PlanPeriod
                                                    {...getPlanPeriodProps(personalPlanContext)}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {ctaType === CareerAcceleratorCTAType.PURCHASE_CTA_MX && (
                                    <div
                                        className={classNames(
                                            'ud-text-xs',
                                            styles['money-back-guarantee'],
                                        )}
                                    >
                                        {gettext('30-Day Money-Back Guarantee')}
                                    </div>
                                )}
                            </div>
                            <div className={styles['course-stats-container']}>
                                <div
                                    className={classNames(
                                        'ud-heading-lg',
                                        styles['course-stats-heading'],
                                    )}
                                >
                                    {gettext("What's included")}
                                </div>
                                <div className={styles['course-stats']}>
                                    <BlockList
                                        size="small"
                                        padding={!isWiderThanMdMin ? 'tight' : 'normal'}
                                    >
                                        <CourseStat
                                            icon={
                                                <Icon
                                                    label={false}
                                                    glyph="trophy"
                                                    size={'medium'}
                                                />
                                            }
                                            value={courses.length}
                                            label={gettext('top-rated courses')}
                                        />
                                        <CourseStat
                                            icon={
                                                <Icon
                                                    label={false}
                                                    className={styles['stat-icon-star']}
                                                    glyph="rating-star"
                                                    size={'medium'}
                                                />
                                            }
                                            value={academy.avgRating?.toLocaleString(undefined, {
                                                maximumFractionDigits: 1,
                                            })}
                                            label={gettext('average course rating')}
                                        />
                                        {(academy.totalExercises ?? 0) > 0 && (
                                            <CourseStat
                                                icon={
                                                    <Icon
                                                        label={false}
                                                        glyph="code"
                                                        size={'medium'}
                                                    />
                                                }
                                                value={academy.totalExercises?.toLocaleString()}
                                                label={
                                                    <>
                                                        {gettext('hands-on practice exercises')}
                                                        <ModalTrigger
                                                            trigger={
                                                                <IconButton
                                                                    className={
                                                                        styles['exercise-info-icon']
                                                                    }
                                                                    size={'small'}
                                                                    udStyle="ghost"
                                                                    aria-label={gettext(
                                                                        'Toggles hands-on practice information modal',
                                                                    )}
                                                                >
                                                                    <Icon
                                                                        label={false}
                                                                        glyph="info-outline"
                                                                        size={'small'}
                                                                        color="subdued"
                                                                    />
                                                                </IconButton>
                                                            }
                                                            renderModal={(props) => (
                                                                <ExerciseInfoModal {...props} />
                                                            )}
                                                        />
                                                    </>
                                                }
                                            />
                                        )}
                                        <CourseStat
                                            icon={
                                                <Icon label={false} glyph="video" size={'medium'} />
                                            }
                                            value={academy.totalContentHours?.toLocaleString(
                                                undefined,
                                                {
                                                    maximumFractionDigits: 1,
                                                },
                                            )}
                                            label={gettext('hours of on-demand content')}
                                        />
                                    </BlockList>
                                </div>
                            </div>
                        </div>
                    </ContentGridItem>
                </ContentGrid>
            </ContentArea>
        </div>
    );
};
